// General
import "./coins-list.scss";
import { useState, useEffect } from "react";
// Services
import {
  useLazyGetProductsListQuery,
  useLazyGetPaymentGatewayListQuery,
  useLazyGetCentrobillCardQuery,
  useLazyGetGenesisCardQuery,
  useLazyGetCardpayCardQuery,
} from "../../../services/data.service";
import { sessionService } from "../../../services/session.service";
// Static Data
import routeConst from "../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateCardDetail,
  updateCentrobillCardDetail,
  updateCardpayCardDetail,
} from "../../../redux/store/paymentStore";
import { updateSelectedCoinPackage } from "../../../redux/store/productsStore";
import {
  updateAddPaymentMethodDialog,
  updateBuyCoinsDialog,
} from "../../../redux/store/dialogStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import useCustomNavigate from "../../utility/custom-hooks/useCustomNavigate-hook";
import IconManager from "../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../elements/spinner/spinner";
import BuyCoinDialog from "../dialog-content/buy-coin-dialog/buy-coin-dialog";

const CoinsList = () => {
  // API variables
  const [
    getProductsList,
    {
      data: getProductsListData,
      error: getProductsListErrorData,
      isFetching: getProductsListFetching,
      isLoading: getProductsListLoading,
      isSuccess: getProductsListSuccess,
      isError: getProductsListError,
    },
  ] = useLazyGetProductsListQuery();
  const [
    getPaymentGatewayList,
    {
      data: paymentGatewayListData,
      error: paymentGatewayListErrorData,
      isFetching: paymentGatewayListFetching,
      isLoading: paymentGatewayListLoading,
      isSuccess: paymentGatewayListSuccess,
      isError: paymentGatewayListError,
    },
  ] = useLazyGetPaymentGatewayListQuery();
  const [
    getCentrobillCard,
    {
      data: getCentrobillCardData,
      error: getCentrobillCardErrorData,
      isFetching: getCentrobillCardFetching,
      isLoading: getCentrobillCardLoading,
      isSuccess: getCentrobillCardSuccess,
      isError: getCentrobillCardError,
    },
  ] = useLazyGetCentrobillCardQuery();
  const [
    getGenesisCard,
    {
      data: getGenesisCardData,
      error: getGenesisCardErrorData,
      isFetching: getGenesisCardFetching,
      isLoading: getGenesisCardLoading,
      isSuccess: getGenesisCardSuccess,
      isError: getGenesisCardError,
    },
  ] = useLazyGetGenesisCardQuery();
  const [
    getCardpayCard,
    {
      data: getCardpayCardData,
      error: getCardpayCardErrorData,
      isFetching: getCardpayCardFetching,
      isLoading: getCardpayCardLoading,
      isSuccess: getCardpayCardSuccess,
      isError: getCardpayCardError,
    },
  ] = useLazyGetCardpayCardQuery();

  // Render variables
  let coinsListView;

  // Redux variables
  const [selectedCoin, setSelectedCoin] = useState(null);
  const isLoggedIn = useSelector((state) => state.public.isLoggedIn);
  const genesisCardDetail = useSelector(
    (state) => state.payment.genesisCardDetail
  );
  const cardpayCardDetail = useSelector(
    (state) => state.payment.cardpayCardDetail
  );
  const centrobillCardDetail = useSelector(
    (state) => state.payment.centrobillCardDetail
  );
  const dispatch = useDispatch();

  // i18next variables
  const { t, i18n } = useTranslation();

  // Custom Hooks Functions
  const onNavigate = useCustomNavigate();
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!isLoggedIn) return;

    getProductsList(`?product_type=coin`, true);
    getPaymentGatewayList("?product_type=coin", true);
    getCentrobillCard(null, true);
    getGenesisCard(null, true);
    getCardpayCard(null, true);
  }, [isLoggedIn]);

  // Lifecycle | Check for update | Unmounted
  useEffect(() => {
    return () => {
      sessionService?.deleteSelectedCoinPackage();
    };
  }, []);

  // Lifecycle | Check for update | Products List API Response
  useEffect(() => {
    if (getProductsListFetching || getProductsListLoading) {
    } else if (getProductsListSuccess) {
      if (sessionService?.getSelectedCoinPackage()) {
        onSelectCoinPackage(sessionService?.getSelectedCoinPackage());
      } else {
        switch (getProductsListData?.status) {
          case 0:
            for (
              let i = 0;
              i < getProductsListData?.data?.product_listings?.length;
              i++
            ) {
              if (getProductsListData?.data?.product_listings[i]?.is_selected) {
                setSelectedCoin(
                  getProductsListData?.data?.product_listings[i]
                    ?.product_hash_id
                );
                break;
              }
            }
            break;
          default:
            break;
        }
      }
    } else if (getProductsListError) {
    }
  }, [
    getProductsListFetching,
    getProductsListLoading,
    getProductsListSuccess,
    getProductsListError,
  ]);

  // Lifecycle | Check for update | Cardpay Card API Response
  useEffect(() => {
    if (getCardpayCardFetching || getCardpayCardLoading) {
    } else if (getCardpayCardSuccess) {
      switch (getCardpayCardData?.status) {
        case 0:
        case 1:
          if (getCardpayCardData?.data?.length > 0) {
            dispatch(updateCardpayCardDetail(getCardpayCardData?.data));
          }
          break;
        default:
          break;
      }
    } else if (getCardpayCardError) {
    }
  }, [
    getCardpayCardFetching,
    getCardpayCardLoading,
    getCardpayCardSuccess,
    getCardpayCardError,
  ]);

  // Lifecycle | Check for update | Centrobill Card API Response
  useEffect(() => {
    if (getCentrobillCardFetching || getCentrobillCardLoading) {
    } else if (getCentrobillCardSuccess) {
      switch (getCentrobillCardData?.status) {
        case 0:
        case 1:
          if (getCentrobillCardData?.data?.length > 0) {
            dispatch(updateCentrobillCardDetail(getCentrobillCardData?.data));
          }
          break;
        default:
          break;
      }
    } else if (getCentrobillCardError) {
    }
  }, [
    getCentrobillCardFetching,
    getCentrobillCardLoading,
    getCentrobillCardSuccess,
    getCentrobillCardError,
  ]);

  // Event Handlers | Button
  const onSelectCoinPackage = (product) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-42-42.1-Button",
      },
    });

    setSelectedCoin(product?.product_hash_id);

    dispatch(updateSelectedCoinPackage(product));

    sessionService?.setSelectedCoinPackage(product);

    if (paymentGatewayListData?.data?.quick_payment_provider === "genesis") {
      if (
        genesisCardDetail?.length > 0 &&
        product?.gateways?.includes("genesis")
      ) {
        onOpenDialog();
      } else {
        dispatch(updateAddPaymentMethodDialog(true));
      }
    } else if (
      paymentGatewayListData?.data?.quick_payment_provider === "cardpay"
    ) {
      if (
        cardpayCardDetail?.length > 0 &&
        product?.gateways?.includes("cardpay")
      ) {
        onOpenDialog();
      } else {
        dispatch(updateAddPaymentMethodDialog(true));
      }
    } else if (
      paymentGatewayListData?.data?.quick_payment_provider === "centrobill"
    ) {
      if (
        centrobillCardDetail?.length > 0 &&
        product?.gateways?.includes("centrobill")
      ) {
        onOpenDialog();
      } else {
        dispatch(updateAddPaymentMethodDialog(true));
      }
    } else {
      dispatch(updateAddPaymentMethodDialog(true));
    }
  };

  // Event Handlers | MUI Dialog
  const onOpenDialog = () => {
    dispatch(updateBuyCoinsDialog(true));
  };

  // Utility Functions
  const getBottomTextBackgroundColor = (colorCode) => {
    switch (colorCode) {
      case "orange_to_red_gradient":
        return "custom-orange-to-red-gradient";
      case "green_gradient":
        return "custom-green-gradient";
      case "pink_gradient":
        return "custom-pink-gradient";
      case "purple_gradient":
        return "custom-purple-gradient";
      case "blue_gradient":
        return "custom-blue-gradient";
      case "yellow_gradient":
        return "custom-yellow-gradient";
      case "dark_purple_gradient":
        return "custom-dark-purple-gradient";
      case "dark_grey_gradient":
        return "custom-grey-to-black-gradient";
      case "dark_green_gradient":
        return "custom-green-to-black-gradient";
      case "dark_maroon_solid":
        return "custom-dark-maroon-solid";
      case "dark_orange_solid":
        return "custom-dark-orange-solid";
      case "peach_pink_solid":
        return "custom-peach-pink-solid";
      case "dark_purple_solid":
        return "custom-dark-purple-solid";
      case "lavender_solid":
        return "custom-lavender-solid";
      case "yellow_solid":
        return "custom-yellow-solid";
      case "midnight_blue":
        return "custom-midnight-blue-solid";
      default:
        return "custom-orange-to-red-gradient";
    }
  };

  // Render Functions | API Response
  if (
    getProductsListFetching ||
    getProductsListLoading ||
    paymentGatewayListFetching ||
    paymentGatewayListLoading ||
    getGenesisCardFetching ||
    getGenesisCardLoading ||
    getCardpayCardFetching ||
    getCardpayCardLoading
  ) {
    return <Spinner />;
  } else if (
    getProductsListSuccess &&
    paymentGatewayListSuccess &&
    getGenesisCardSuccess &&
    getCardpayCardSuccess
  ) {
    if (getProductsListData?.status === 0) {
      if (getProductsListData?.data?.product_listings?.length > 0) {
        coinsListView = getProductsListData?.data?.product_listings?.map(
          (coin, index) => {
            return (
              <div
                className={`coins-container ${
                  selectedCoin === coin?.product_hash_id ? "selected-coin" : ""
                }`}
                style={{
                  background:
                    selectedCoin === coin?.product_hash_id
                      ? coin?.params?.main_selected_background_color
                      : coin?.params?.main_background_color,
                  borderColor:
                    selectedCoin === coin?.product_hash_id
                      ? coin?.params?.main_selected_border_color
                      : coin?.params?.main_border_color,
                }}
                onClick={() => onSelectCoinPackage(coin)}
                key={index}
              >
                <div className="first-row">
                  <div className="limited-time-badge">
                    <img
                      className="limited-time-image"
                      src={coin?.params?.top_left_sticker}
                    />
                  </div>

                  <div className="savings-container">
                    {coin?.params?.coin_extra_amount && (
                      <div
                        className="note-1"
                        style={{
                          background: coin?.params?.coin_extra_background_color,
                          color: coin?.params?.coin_extra_text_color,
                        }}
                      >
                        {coin?.params?.coin_extra_amount}
                      </div>
                    )}

                    {coin?.discount_text && (
                      <div
                        className="discount-text"
                        style={{
                          background:
                            coin?.params?.discount_text_background_color,
                          color: coin?.params?.discount_text_color,
                        }}
                      >
                        {coin?.discount_text}
                      </div>
                    )}
                  </div>
                </div>

                <div className="second-row">
                  <div className="coin-image-container">
                    {getIcon("sbCoinIcon", "coin-image")}
                  </div>

                  <div
                    className="quantity-label"
                    style={{ color: coin?.params?.quantity_text_color }}
                  >
                    {coin?.quantity?.toLocaleString()}
                  </div>
                </div>

                <div className="third-row">
                  <div
                    className={`price-label ${
                      coin?.discounted_amount ? "discounted-price-label" : ""
                    }`}
                    style={{
                      color:
                        coin?.params?.display_currency_text_color ??
                        coin?.params?.amount_text_color,
                    }}
                  >
                    {coin?.display_string}
                    {/* {coin?.display_currency}{" "}
                  {coin?.discounted_amount?.toLocaleString() ??
                    coin?.amount?.toLocaleString()} */}
                  </div>
                </div>

                <div className="fourth-row">
                  {coin?.discounted_amount ? (
                    <div
                      className="original-price-label"
                      style={{ color: coin?.params?.amount_text_color }}
                    >
                      {coin?.original_string}
                    </div>
                  ) : null}
                </div>

                {coin?.params?.bottom_floating_text && (
                  <div
                    className={`bottom-floating-text ${getBottomTextBackgroundColor(
                      coin?.params?.bottom_floating_background_color
                    )}`}
                    style={{
                      color: coin?.params?.bottom_floating_text_color,
                    }}
                  >
                    {coin?.params?.bottom_floating_text}
                  </div>
                )}
              </div>
            );
          }
        );
      }
    }
  } else if (
    getProductsListError ||
    paymentGatewayListError ||
    getGenesisCardError ||
    getCardpayCardError
  ) {
    if (
      getProductsListErrorData?.status === 401 ||
      getProductsListErrorData?.status === 401
    ) {
      onNavigate(routeConst.logout.path);
    }
  }

  return (
    <div id="coins-list-shared-subcomponent">
      {coinsListView}

      <BuyCoinDialog />
    </div>
  );
};

export default CoinsList;
